// src/components/Products.jsx
import { AnimatePresence, motion } from 'framer-motion';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from 'react';
import { FaExternalLinkAlt, FaInfoCircle } from "react-icons/fa";
import { TiArrowUp, TiLocationArrow } from "react-icons/ti";
import { useNavigate } from 'react-router-dom';
import products from '../data/productsData';
import AnimatedTitle from './AnimatedTitle';
import Footer from './Footer';
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

// Register ScrollTrigger once
gsap.registerPlugin(ScrollTrigger);

/** 
 * Helper function to parse all unique sub-sectors out of productsData,
 * splitting on "/" and trimming each sub-sector, e.g.
 * "FinTech / DeFi / Crypto Wallet" => ["FinTech", "DeFi", "Crypto Wallet"].
 */
function getUniqueSectors(productsArray) {
  const sectorSet = new Set();
  productsArray.forEach((prod) => {
    // Split on "/" and trim
    const subSectors = prod.sector.split("/").map(item => item.trim());
    subSectors.forEach(sub => sectorSet.add(sub));
  });
  // Convert set to sorted array
  return Array.from(sectorSet).sort();
}

const Products = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState('All');
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [isSectorDropdownOpen, setIsSectorDropdownOpen] = useState(false);

  // We dynamically determine all sub-sector options from the products array
  const [sectorOptions, setSectorOptions] = useState([]);
  
  // Refs for layout & GSAP
  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);
  const productsRef = useRef(null);
  const introRef = useRef(null);
  const productCardRefs = useRef([]); 

  // On mount, compute our dynamic drop-down options
  useEffect(() => {
    const uniqueSectors = getUniqueSectors(products);
    setSectorOptions(uniqueSectors);
  }, []);

  // ---------------------- FILTERED PRODUCTS ----------------------
  const filteredProducts = products.filter((product) => {
    // Combine name, sector, and description for search convenience
    const fullText = [
      product.name,
      product.sector,
      product.description
    ].join(' ').toLowerCase();

    // The search box matches any substring in name/sector/description
    const matchesSearch = fullText.includes(searchTerm.toLowerCase());

    // The drop-down filters by a single sub-sector (partial match).
    // If "All" is selected, we skip the sector filter.
    const sectorFilterPass =
      selectedSector === 'All'
        ? true
        : product.sector.toLowerCase().includes(selectedSector.toLowerCase());

    return matchesSearch && sectorFilterPass;
  });

  // ---------------------- GSAP + HOOKS ----------------------------
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Hero Section (fade in from top)
    if (sectionRefs.current[0]) {
      gsap.fromTo(
        sectionRefs.current[0],
        { opacity: 0, y: -50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRefs.current[0],
            start: "top 80%",
          },
        }
      );
    }

    // Products Section (fade in from bottom)
    if (productsRef.current) {
      gsap.fromTo(
        productsRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: productsRef.current,
            start: "top 80%",
          },
        }
      );
    }

    // Scroll Down Button
    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
        }
      );
    }

    // Back to Top Button
    if (scrollTopRef.current) {
      gsap.fromTo(
        scrollTopRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power3.out",
          scrollTrigger: {
            trigger: scrollTopRef.current,
            start: "top bottom",
          },
        }
      );
    }
  }, []);

  // Animate product cards on load or whenever the filtered array changes
  useEffect(() => {
    if (!productsRef.current || productCardRefs.current.length === 0) return;

    // Fade in first 3 cards immediately
    gsap.fromTo(
      productCardRefs.current.slice(0, 3).filter(Boolean),
      { opacity: 0, y: 0 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    // Stagger the rest
    gsap.fromTo(
      productCardRefs.current.slice(3).filter(Boolean),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: productsRef.current,
          start: "top 90%",
          end: "bottom top",
          scrub: false,
          once: true,
        },
      }
    );
  }, [filteredProducts]);



  // ---------------------- EVENT HANDLERS ---------------------------
  const handleMoreDetailsClick = (id) => {
    navigate(`/products/${id}`);
  };

  const handleViewOnChainClick = (link) => {
    window.open(link, '_blank');
  };

  const checkIfBottom = () => {
    // Show "Back to Top" if near page bottom
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const scrollToContent = () => {
    if (introRef.current) {
      const top = introRef.current.offsetTop - 80; 
      window.scrollTo({ top, behavior: "smooth" });
      setScrollDownClicked(true);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  // ---------------------- INPUT HANDLERS ----------------------------
  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSectorChange = (e) => setSelectedSector(e.target.value);

  // ---------------------- RENDER -----------------------------------
  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section - Full Viewport Height */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
      
        {/* Hero Video Container */}
        <div
          className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]"
          ref={(el) => (sectionRefs.current[0] = el)}
        >
          <video
            src="/videos/cryptostore-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {/* Sparkles overlay */}
            <SparklesCore
              id="productsHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
              <AnimatedTitle
                title="Our Products"
                containerClass="text-center text-blue-50 font-zentry relative z-10"
              />
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
              >
                Cutting-Edge Suite · Modular Ecosystem · User-Centric Design
              </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button - Fixed at bottom center */}
      {!scrollDownClicked && (
        <ScrollDownButton
          targetId="products-section"
          onClick={scrollToContent}
          ref={scrollDownRef}
          additionalClasses=""
        />
      )}

      {/* Intro Text Section */}
      <div className="w-full max-w-4xl mx-auto text-center px-5 py-10" id="intro-section" ref={introRef}>
        <motion.h3 
          className="text-lg md:text-2xl font-bold tracking-wide bg-gradient-to-r from-cyan-200 via-white to-cyan-200 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Explore the Innovation Behind Zero-Blockchain
        </motion.h3>
        <motion.p 
          className="mt-2 text-sm md:text-lg text-gray-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Discover our suite of products designed to revolutionise the blockchain 
          and fintech industries, driving innovation and efficiency.
        </motion.p>
      </div>

      {/* Search and Filter Section */}
      <div className="w-full max-w-7xl mx-auto px-5 py-10" id="products-section" ref={productsRef}>
        {/* Enhanced Search Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mb-8"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl blur-xl" />
          <div className="relative flex flex-col md:flex-row gap-4 p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl z-20">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search products (name, sector, etc.)..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                aria-label="Search products"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Custom Aceternity-styled sector mega dropdown */}
            <div className="relative md:w-64">
              <button
                onClick={() => setIsSectorDropdownOpen(!isSectorDropdownOpen)}
                className="w-full flex items-center justify-between gap-2 p-4 rounded-xl bg-white/5 border border-white/10 text-white hover:border-cyan-500/50 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
              >
                <span className={selectedSector === 'All' ? 'text-gray-300' : 'text-white'}>
                  {selectedSector === 'All' ? 'All Sectors' : selectedSector}
                </span>
                <TiLocationArrow className={`text-cyan-400 transition-transform duration-300 ${isSectorDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {isSectorDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute right-0 mt-2 w-[500px] z-50"
                  >
                    {/* Animated gradient border container */}
                    <div className="relative rounded-2xl p-[1px] overflow-hidden">
                      {/* Animated gradient border */}
                      <motion.div
                        className="absolute inset-0 rounded-2xl"
                        style={{
                          background: "linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6, #ec4899, #06b6d4)",
                          backgroundSize: "200% 100%",
                        }}
                        animate={{
                          backgroundPosition: ["0% 50%", "200% 50%"],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />
                      
                      {/* Outer glow */}
                      <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-cyan-500/30 via-blue-500/20 to-purple-500/30 blur-xl opacity-50" />
                      
                      {/* Inner content container */}
                      <div className="relative bg-black/95 backdrop-blur-xl rounded-2xl overflow-hidden">
                        {/* Spotlight effects */}
                        <div className="absolute top-0 left-1/4 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
                        
                        <div className="relative z-10 p-4">
                          {/* All Sectors button */}
                          <motion.button
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0 }}
                            className={`group relative flex items-center justify-center w-full px-4 py-3 mb-4 text-center rounded-xl overflow-hidden transition-all duration-300 ${selectedSector === 'All' ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white' : 'bg-white/5 border border-white/10 text-gray-300 hover:border-cyan-500/50'}`}
                            onClick={() => {
                              setSelectedSector('All');
                              setIsSectorDropdownOpen(false);
                            }}
                          >
                            <span className="relative z-10 font-medium">✨ Show All Products</span>
                          </motion.button>
                          
                          {/* Category Grid */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* FinTech Column */}
                            <div>
                              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-400/70 flex items-center gap-2 mb-2">
                                <span className="w-2 h-[1px] bg-gradient-to-r from-transparent to-cyan-400/50" />
                                FinTech & Banking
                              </div>
                              {['FinTech', 'BankTech', 'PayTech', 'LendTech', 'WealthTech', 'InsurTech'].map((option, index) => (
                                sectorOptions.includes(option) && (
                                  <motion.button
                                    key={option}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.02 }}
                                    className={`group relative flex items-center w-full px-3 py-1.5 text-left text-sm rounded-lg overflow-hidden transition-all duration-300 ${selectedSector === option ? 'text-cyan-400 bg-cyan-500/10' : 'text-gray-300'}`}
                                    onClick={() => {
                                      setSelectedSector(option);
                                      setIsSectorDropdownOpen(false);
                                    }}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gradient-to-b from-cyan-400 to-blue-500 rounded-full group-hover:h-3 transition-all duration-300" />
                                    <span className="relative z-10 pl-2 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">{option}</span>
                                  </motion.button>
                                )
                              ))}
                            </div>
                            
                            {/* Blockchain Column */}
                            <div>
                              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-purple-400/70 flex items-center gap-2 mb-2">
                                <span className="w-2 h-[1px] bg-gradient-to-r from-transparent to-purple-400/50" />
                                Blockchain & DeFi
                              </div>
                              {['Blockchain', 'DeFi', 'Crypto Wallet', 'Decentralised Exchange', 'Asset Management'].map((option, index) => (
                                sectorOptions.includes(option) && (
                                  <motion.button
                                    key={option}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.02 }}
                                    className={`group relative flex items-center w-full px-3 py-1.5 text-left text-sm rounded-lg overflow-hidden transition-all duration-300 ${selectedSector === option ? 'text-purple-400 bg-purple-500/10' : 'text-gray-300'}`}
                                    onClick={() => {
                                      setSelectedSector(option);
                                      setIsSectorDropdownOpen(false);
                                    }}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gradient-to-b from-purple-400 to-pink-500 rounded-full group-hover:h-3 transition-all duration-300" />
                                    <span className="relative z-10 pl-2 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">{option}</span>
                                  </motion.button>
                                )
                              ))}
                            </div>
                            
                            {/* AI & Data Column */}
                            <div>
                              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-400/70 flex items-center gap-2 mb-2">
                                <span className="w-2 h-[1px] bg-gradient-to-r from-transparent to-emerald-400/50" />
                                AI & Analytics
                              </div>
                              {['AI', 'Data Analysis', 'Trading', 'Stock Trading', 'Fraud Detection', 'Markets'].map((option, index) => (
                                sectorOptions.includes(option) && (
                                  <motion.button
                                    key={option}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.02 }}
                                    className={`group relative flex items-center w-full px-3 py-1.5 text-left text-sm rounded-lg overflow-hidden transition-all duration-300 ${selectedSector === option ? 'text-emerald-400 bg-emerald-500/10' : 'text-gray-300'}`}
                                    onClick={() => {
                                      setSelectedSector(option);
                                      setIsSectorDropdownOpen(false);
                                    }}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/0 via-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gradient-to-b from-emerald-400 to-cyan-500 rounded-full group-hover:h-3 transition-all duration-300" />
                                    <span className="relative z-10 pl-2 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">{option}</span>
                                  </motion.button>
                                )
                              ))}
                            </div>
                            
                            {/* RegTech Column */}
                            <div>
                              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-orange-400/70 flex items-center gap-2 mb-2">
                                <span className="w-2 h-[1px] bg-gradient-to-r from-transparent to-orange-400/50" />
                                RegTech & Other
                              </div>
                              {['RegTech', 'SupTech', 'GreenTech', 'Payment Integration'].map((option, index) => (
                                sectorOptions.includes(option) && (
                                  <motion.button
                                    key={option}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.02 }}
                                    className={`group relative flex items-center w-full px-3 py-1.5 text-left text-sm rounded-lg overflow-hidden transition-all duration-300 ${selectedSector === option ? 'text-orange-400 bg-orange-500/10' : 'text-gray-300'}`}
                                    onClick={() => {
                                      setSelectedSector(option);
                                      setIsSectorDropdownOpen(false);
                                    }}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gradient-to-b from-orange-400 to-red-500 rounded-full group-hover:h-3 transition-all duration-300" />
                                    <span className="relative z-10 pl-2 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">{option}</span>
                                  </motion.button>
                                )
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Count of filtered products */}
        <div className="flex items-center gap-3 mb-8">
          <span className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 text-cyan-400 text-sm font-medium">
            {filteredProducts.length} Products Found
          </span>
        </div>

        {/* Products Grid */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          style={{ perspective: "1200px" }}
        >
          {filteredProducts.map((product, i) => (
            <motion.div
              key={product.id}
              ref={(el) => (productCardRefs.current[i] = el)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              className="group relative overflow-hidden flex flex-col transition-all duration-500 hover:shadow-xl hover:shadow-cyan-500/10"
              style={{
                borderRadius: '1rem',
                transformStyle: "preserve-3d",
              }}
              onMouseMove={(e) => {
                if (!e.currentTarget) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                const rotateX = ((y - centerY) / centerY) * 5;
                const rotateY = ((x - centerX) / centerX) * 5;
                gsap.to(e.currentTarget, {
                  rotateX: -rotateX,
                  rotateY: rotateY,
                  scale: 1.02,
                  duration: 0.3,
                  overwrite: 'auto'
                });
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget) return;
                gsap.to(e.currentTarget, {
                  rotateX: 0,
                  rotateY: 0,
                  scale: 1,
                  duration: 0.5,
                  ease: "power2.out",
                  overwrite: 'auto'
                });
              }}
            >
              {/* Animated gradient border */}
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-cyan-500/50 via-purple-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-cyan-500/30 via-purple-500/30 to-pink-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Card content */}
              <div className="relative flex flex-col h-full bg-gradient-to-b from-gray-900/95 via-gray-900/98 to-black rounded-2xl border border-white/10 group-hover:border-transparent transition-colors duration-500 overflow-hidden">
                {/* Spotlight effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-radial from-cyan-500/20 to-transparent rounded-full blur-2xl" />
                </div>
                
                {/* Product Image */}
                <div className="relative h-52 flex items-center justify-center bg-black/50 border-b border-white/10 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900/80" />
                  <img
                    src={product.image}
                    alt={product.name}
                    className="relative z-10 object-contain transition-transform duration-500 group-hover:scale-110"
                    style={{
                      maxHeight: "85%",
                      maxWidth: "85%",
                    }}
                  />
                </div>

                {/* Content section */}
                <div className="relative z-10 p-5 flex-grow flex flex-col">
                  {/* Title */}
                  <h2 className="text-xl font-bold mb-3 text-center bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent">
                    {product.name}
                  </h2>
                  
                  {/* Sector badge */}
                  <div className="flex justify-center mb-3">
                    <span className="px-3 py-1 rounded-full text-xs bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
                      {product.sector}
                    </span>
                  </div>
                  
                  {/* Description */}
                  <div className="flex-grow bg-white/5 rounded-xl p-4 mb-4 border border-white/5">
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {product.description}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <GlassButton
                      onClick={() => handleMoreDetailsClick(product.id)}
                      ariaLabel={`More details about ${product.name}`}
                      variant="gray"
                      size="sm"
                      className="flex-1"
                    >
                      <FaInfoCircle className="text-xs" />
                      More Details
                    </GlassButton>
                    
                    <GlassButton
                      onClick={() => handleViewOnChainClick(product.link)}
                      ariaLabel={`Go to ${product.name}`}
                      variant="cyan"
                      size="sm"
                      className="flex-1"
                    >
                      <FaExternalLinkAlt className="text-xs" />
                      Go to Product
                    </GlassButton>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp
            className="text-cyan-400 transition-all duration-500"
            size={24}
          />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Products;
